import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import styled from 'styled-components';

import { TaggedText } from 'components/TaggedText';
import { ChatMessage } from 'services/api/chat';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

type Props = {
  message: ChatMessage;
  // Own messages align right with a tinted bubble; others align left with a gray bubble.
  isOwn: boolean;
};

// The broadcast annotation copy, isolated so the wording is a one-line change. `sentToCount`
// is the sender's ACTUAL reach; partial-access wording ("Sent to 5 buildings") is pending
// design (CHAT_CORE_MECHANISM §Broadcast), so both cases render "Sent to all {N} buildings"
// for now.
export function broadcastAnnotation(count: number): string {
  return `Sent to all ${count} buildings`;
}

// Matches the mockup's "Today 9:04 AM" (no "at"); relative for today/yesterday, else dated.
function formatMessageTime(iso: string): string {
  const date = parseISO(iso);
  if (isNaN(date.getTime())) return '';
  const days = differenceInCalendarDays(date, new Date());
  if (days === 0) return format(date, "'Today' h:mm a");
  if (days === -1) return format(date, "'Yesterday' h:mm a");
  return format(date, 'MM/dd/yyyy h:mm a');
}

// One message: a sender + timestamp header above a bubble. Mentions render as chips via the
// shared TaggedText parser (never raw HTML); plain/older rows fall back to plaintext.
export default function MessageBubble({ message, isOwn }: Props) {
  return (
    <Row $own={isOwn} data-testid='chat-message'>
      <Meta $own={isOwn}>
        <Sender>{message.createdBy.name}</Sender>
        <Timestamp>{formatMessageTime(message.createdAt)}</Timestamp>
      </Meta>
      <Bubble $own={isOwn}>
        <TaggedText text={message.text ?? message.plaintext} $archived={false} />
      </Bubble>
      {message.sentToCount != null && (
        <Annotation data-testid='chat-broadcast-annotation'>{broadcastAnnotation(message.sentToCount)}</Annotation>
      )}
    </Row>
  );
}

const Row = styled.div<{ $own: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $own }) => ($own ? 'flex-end' : 'flex-start')};
  gap: 4px;
  max-width: 100%;
`;

const Meta = styled.div<{ $own: boolean }>`
  display: flex;
  flex-direction: ${({ $own }) => ($own ? 'row-reverse' : 'row')};
  align-items: baseline;
  gap: 8px;
`;

const Sender = styled(BodySmallBold)`
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
`;

// A div (not BodySmall) so it can host TaggedText, which is itself a BodySmall <p> — nesting
// <p> in <p> is invalid. Typography comes from TaggedText; the bubble owns the chrome + color.
const Bubble = styled.div<{ $own: boolean }>`
  max-width: 76%;
  padding: 10px 14px;
  border-radius: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${colors.black100};
  background: ${({ $own }) => ($own ? colors.blue10 : colors.black5)};
`;

// The "Sent to all N buildings" note sits just beneath the bubble, aligned with it (the Row
// already handles own-vs-other alignment) — muted, like the mockup's Mary Williams example.
const Annotation = styled(BodySmall)`
  color: ${colors.black50};
`;
